/**
 * computeSeatMap — derive the lobby's seat list (1..maxPlayers) from the
 * players + invitations the API already sends. Mirrors the server's slot model
 * (routes/games/shared.ts computeSlotMap): a seat is FILLED by a joined player,
 * RESERVED by a pending invite that pinned that slot (only its invitee may take
 * it), or OPEN. Used by the gathering UI to show filled / reserved / open seats.
 */

import type { GamePlayerResponse, GameInvitationResponse } from '@warcarp/api-types'

export type Seat =
  | { slot: number; kind: 'filled'; player: GamePlayerResponse }
  | { slot: number; kind: 'reserved'; inviteeName: string; invitationId: string }
  | { slot: number; kind: 'open' }

export function computeSeatMap(
  players: GamePlayerResponse[],
  invitations: GameInvitationResponse[],
  maxPlayers: number,
): Seat[] {
  const playerBySlot = new Map<number, GamePlayerResponse>()
  for (const p of players) playerBySlot.set(p.slot, p)

  const inviteBySlot = new Map<number, { name: string; id: string }>()
  for (const inv of invitations) {
    if (inv.status !== 'pending' || inv.slot == null) continue
    // A slot that's already filled by a player can't also be reserved.
    if (playerBySlot.has(inv.slot)) continue
    inviteBySlot.set(inv.slot, { name: inv.user.displayName, id: inv.id })
  }

  const seats: Seat[] = []
  for (let slot = 1; slot <= maxPlayers; slot++) {
    const player = playerBySlot.get(slot)
    if (player) {
      seats.push({ slot, kind: 'filled', player })
      continue
    }
    const invite = inviteBySlot.get(slot)
    if (invite !== undefined) {
      seats.push({ slot, kind: 'reserved', inviteeName: invite.name, invitationId: invite.id })
      continue
    }
    seats.push({ slot, kind: 'open' })
  }
  return seats
}
